import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { specials } from '@/mocks/specials';
import { useSeo } from '@/hooks/useSeo';

export default function Special() {
  const [activeId, setActiveId] = useState(specials[0].id);
  const active = specials.find((s) => s.id === activeId) ?? specials[0];

  useSeo(
    `${active.title} - 专题 - 十八禁影视`,
    `${active.summary.slice(0, 70)}。专题包含策划片单与推荐理由，帮助你系统了解相关主题作品。`,
  );

  const playlist = useMemo(
    () =>
      active.movieIds
        .map((id) => movies.find((m) => m.id === id))
        .filter((m): m is (typeof movies)[number] => Boolean(m)),
    [active],
  );

  const relatedSpecials = specials.filter((s) => s.id !== active.id);

  return (
    <div className="w-full pt-16 md:pt-20">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-10 bg-background-100/50 border-b border-background-200/60">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">精选专题</span>
        </nav>
        <p className="font-label text-xs text-accent-400 tracking-[0.28em] mb-3">EDITORIAL COLLECTIONS</p>
        <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">精选专题</h1>
        <p className="mt-3 text-sm text-foreground-600 max-w-2xl leading-relaxed">
          每个专题围绕一个主题或风格展开，由编辑团队挑选作品并附上推荐理由。点击下方标签切换不同专题，系统了解一片内容。
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {specials.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveId(s.id)}
              className={`inline-flex items-center gap-2 text-xs md:text-sm rounded-md px-4 py-2.5 whitespace-nowrap cursor-pointer border transition-colors ${
                s.id === activeId
                  ? 'bg-primary-500 border-primary-500 text-foreground-950'
                  : 'bg-background-100 border-background-200 text-foreground-700 hover:border-background-300'
              }`}
            >
              <span className="font-label">0{i + 1}</span>
              {s.title.split('：')[0]}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div key={active.id} className="animate-fade-in">
          {/* 专题主图与介绍 */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 items-start">
            <div className="lg:col-span-3 rounded-lg overflow-hidden border border-background-200/70 bg-background-300">
              <img
                src={active.cover}
                alt={`${active.title} 专题主图`}
                title={`${active.title} - 十八禁影视专题`}
                className="w-full aspect-[16/9] object-cover object-center"
              />
            </div>
            <div className="lg:col-span-2">
              <span className="inline-block bg-accent-100 text-accent-900 text-xs px-2.5 py-1 rounded-md font-label mb-3">
                {playlist.length} 部片单
              </span>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 font-bold leading-snug">
                {active.title}
              </h2>
              <p className="mt-4 text-sm text-foreground-700 leading-relaxed">{active.summary}</p>
            </div>
          </div>

          {/* 推荐理由 */}
          <div className="mt-8 bg-primary-500/10 border border-primary-500/25 rounded-lg p-5 md:p-6">
            <h3 className="font-heading text-base text-foreground-950 font-bold mb-3 flex items-center gap-2">
              <i className="ri-quill-pen-line text-accent-400" />
              推荐理由
            </h3>
            <p className="text-sm text-foreground-700 leading-relaxed">{active.reason}</p>
          </div>

          {/* 策划片单 */}
          <div className="mt-12">
            <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-bold mb-6 flex items-center gap-3">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              策划片单
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {playlist.map((movie, i) => (
                <MovieCard key={movie.id} movie={movie} index={i} />
              ))}
            </div>
          </div>

          {/* 相关专题 */}
          <div className="mt-12">
            <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-bold mb-6 flex items-center gap-3">
              <span className="w-1 h-5 bg-accent-500 rounded-full" />
              相关专题
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {relatedSpecials.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setActiveId(s.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="group flex flex-col sm:flex-row items-stretch gap-4 text-left bg-background-100 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-500/60 transition-colors p-4"
                >
                  <span className="w-full sm:w-40 shrink-0 rounded-md overflow-hidden bg-background-300">
                    <img
                      src={s.cover}
                      alt={`${s.title} 专题封面`}
                      title={`${s.title} - 十八禁影视专题`}
                      className="w-full aspect-[16/9] sm:aspect-[4/3] object-cover object-center"
                    />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-base text-foreground-950 font-bold group-hover:text-primary-400 transition-colors">
                      {s.title}
                    </span>
                    <span className="block mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">
                      {s.summary}
                    </span>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs text-accent-400">
                      切换查看 <i className="ri-arrow-right-line" />
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}